import { Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import { settle, start } from "../../api/asyncData";
import { getEntries } from "../../api/commands/entries";
import {
	addRecord,
	checkProfileImageExists,
	editRecord,
	getRecord,
	getRecords,
	importProfileImageFromUrl,
	lookupRecord,
} from "../../api/commands/records";
import { ApiError, toMessage } from "../../api/errors";
import type { RecordSummary } from "../../api/schemas/records";
import { readCapturePayload } from "../../capture/payloadService";
import {
	Capture,
	createBody,
	decodePayload,
	linkOverlay,
	selectByName,
	syncOverlay,
	syncedFields,
} from "../../core/captureMatch";
import * as Picker from "../../widgets/recordPicker";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import { type Model, type Selected, initial } from "./model";

// COMMANDS

/** KTD3: the payload comes from the boot-time stash, never the URL; storage is I/O, so it is read in a Command. */
export const ReadPayload = Command.define("ReadCapturePayload", {
	messages: [Message.CompletedReadPayload],
	execute: Effect.sync(() =>
		Message.CompletedReadPayload({
			raw: readCapturePayload(window.sessionStorage),
		}),
	),
});
export const Lookup = lookupRecord.command(
	Message.CompletedLookup,
	({ generation, result }) => Message.CompletedLookup({ generation, result }),
);
export const NameSearch = getRecords.command(
	Message.CompletedNameSearch,
	({ generation, result }) =>
		Message.CompletedNameSearch({ generation, result }),
);
export const FetchRecord = getRecord.command(
	Message.CompletedFetchRecord,
	({ nodeId, generation, result }) =>
		Message.CompletedFetchRecord({ nodeId, generation, result }),
);
export const FetchEntries = getEntries.command(
	Message.CompletedFetchEntries,
	({ nodeId, generation, result }) =>
		Message.CompletedFetchEntries({ nodeId, generation, result }),
);
export const CheckImage = checkProfileImageExists.command(
	Message.CompletedCheckImage,
	({ nodeId, generation, result }) =>
		Message.CompletedCheckImage({ nodeId, generation, result }),
);
export const Link = editRecord.command(
	Message.CompletedLink,
	({ generation, result }) => Message.CompletedLink({ generation, result }),
);
export const SyncPatch = editRecord.command(
	Message.CompletedSyncPatch,
	({ generation, result }) =>
		Message.CompletedSyncPatch({ generation, result }),
);
export const ImportPhoto = importProfileImageFromUrl.command(
	Message.CompletedPhoto,
	({ nodeId, generation, result }) =>
		Message.CompletedPhoto({ nodeId, generation, result }),
);

const createdId = (body: unknown): Result.Result<string, ApiError> =>
	typeof body === "object" &&
	body !== null &&
	"created" in body &&
	typeof body.created === "string"
		? Result.succeed(body.created)
		: Result.fail(ApiError.Decode({ path: "created" }));

/** R7: `addRecord` with the LinkedIn fields and identity; the timestamp is taken at issue time, as the Records page does. */
export const Create = Command.define("CreateCaptureRecord", {
	args: { capture: Capture, generation: S.Number },
	messages: [Message.CompletedCreate],
	execute: ({ capture, generation }) =>
		addRecord.run({ data: createBody(capture, new Date().toISOString()) }).pipe(
			Effect.map((result) =>
				Message.CompletedCreate({
					generation,
					result: Result.flatMap(result, createdId),
				}),
			),
		),
});

// HELPERS

type Return = PageReturn<Model, Message>;
type Step = (model: Model) => Return;

const isStale = (
	model: Model,
	nodeId: string,
	generation: number,
	shared: Shared,
): boolean =>
	generation !== shared.generation ||
	!Option.exists(model.selected, (selected) => selected.id === nodeId);

/** KTD6, KTD7: any selection fetches the full record (the PATCH base), its entries, and its avatar. */
const select = (model: Model, selected: Selected, shared: Shared): Return => ({
	model: evo(model, {
		selected: () => Option.some(selected),
		resolving: () => false,
		record: () => AsyncData.Loading(),
		entries: () => AsyncData.Loading(),
		image: () => AsyncData.Loading(),
		picker: () => Picker.init,
	}),
	commands: [
		FetchRecord({
			nodeId: selected.id,
			asOf: null,
			generation: shared.generation,
		}),
		// ponytail: first page only; a record with more task entries than one page under-reports open tasks.
		FetchEntries({
			nodeId: selected.id,
			search: "",
			cursor: 0,
			asOf: null,
			generation: shared.generation,
		}),
		CheckImage({ nodeId: selected.id, generation: shared.generation }),
	],
});

const refetchRecord =
	(shared: Shared): Step =>
	(model) =>
		Option.match(model.selected, {
			onNone: () => ({ model }),
			onSome: ({ id }) => ({
				model: evo(model, { record: start, image: start }),
				commands: [
					FetchRecord({
						nodeId: id,
						asOf: null,
						generation: shared.generation,
					}),
					CheckImage({ nodeId: id, generation: shared.generation }),
				],
			}),
		});

const lookup = (model: Model, capture: Capture, shared: Shared): Return => ({
	model: evo(model, { resolving: () => true }),
	commands: [
		Lookup({
			system: "linkedin",
			value: capture.url,
			generation: shared.generation,
		}),
	],
});

/**
 * A write failed. A 409 means the identity already sits on another record (KTD6): look that one up and select it.
 * A 412 means the record changed since it was fetched (#7): reload it so the next write overlays the current version.
 */
const failedWrite = (model: Model, error: ApiError, shared: Shared): Return => {
	const settled = evo(model, { inFlight: () => Option.none() });
	if (error._tag === "Http" && error.status === 412)
		return refetchRecord(shared)(
			evo(settled, { stale: () => true, error: () => Option.none() }),
		);
	return error._tag === "Http" &&
		error.status === 409 &&
		Option.isSome(model.payload)
		? lookup(
				evo(settled, { conflict: () => true, error: () => Option.none() }),
				model.payload.value,
				shared,
			)
		: { model: evo(settled, { error: () => Option.some(toMessage(error)) }) };
};

/** R8: the photo is best-effort and only when the capture had one; without one the chain is done. */
const maybeImportPhoto =
	(shared: Shared): Step =>
	(model) => {
		const photo = Option.flatMap(model.payload, (capture) =>
			Option.fromNullishOr(capture.photo),
		);
		if (Option.isSome(photo) && Option.isSome(model.selected)) {
			return {
				model,
				commands: [
					ImportPhoto({
						nodeId: model.selected.value.id,
						url: photo.value,
						generation: shared.generation,
					}),
				],
			};
		}
		return { model: evo(model, { inFlight: () => Option.none() }) };
	};

/** After a Sync PATCH the record on screen is stale: the photo step when there is one, else a refetch. */
const afterFieldsSaved =
	(shared: Shared): Step =>
	(model) =>
		Option.exists(model.payload, (capture) => capture.photo !== undefined)
			? maybeImportPhoto(shared)(model)
			: refetchRecord(shared)(evo(model, { inFlight: () => Option.none() }));

/** Writers need a selection, a settled `getRecord` response as the base, the capture, and no chain in flight. */
const withBase = (
	model: Model,
	f: (id: string, record: RecordSummary, capture: Capture) => Return,
): Return =>
	Option.isSome(model.inFlight) ||
	Option.isNone(model.selected) ||
	Option.isNone(model.payload) ||
	!AsyncData.isSuccess(model.record)
		? { model }
		: f(model.selected.value.id, model.record.data, model.payload.value);

const foldPicker = (shared: Shared) =>
	Update.foldChild({
		update: (child: Picker.Model, message: Picker.Message) =>
			Picker.update(child, message, shared),
		read: (model: Model) => Option.some(model.picker),
		write: (model: Model, picker: Picker.Model) =>
			evo(model, { picker: () => picker }),
		toParentMessage: (message: Picker.Message) =>
			Message.GotPickerMessage({ message }),
	});

// PAGE

export const page: Page<Model, Message> = {
	init: () => ({ model: initial, commands: [ReadPayload()] }),
	changedRoute: (model) => ({ model }),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedReadPayload: ({ raw }) => {
				const payload = Option.flatMap(raw, (fragment) =>
					Option.fromNullishOr(decodePayload(fragment)),
				);
				const read = evo(model, {
					payload: () => payload,
					resolving: () => false,
				});
				return Option.match(payload, {
					onNone: () => ({ model: read }),
					onSome: (capture) => lookup(read, capture, shared),
				});
			},
			CompletedLookup: ({ generation, result }) => {
				if (generation !== shared.generation) return { model };
				return Result.match(result, {
					onSuccess: (record) =>
						select(model, { id: record.id, by: "url" }, shared),
					onFailure: (error) =>
						error._tag === "NotFound" && Option.isSome(model.payload)
							? {
									model,
									commands: [
										NameSearch({
											search: model.payload.value.name,
											cursor: 0,
											asOf: null,
											limit: 100,
											generation: shared.generation,
										}),
									],
								}
							: {
									model: evo(model, {
										resolving: () => false,
										error: () => Option.some(toMessage(error)),
									}),
								},
				});
			},
			CompletedNameSearch: ({ generation, result }) => {
				if (generation !== shared.generation || Option.isNone(model.payload))
					return { model };
				const capture = model.payload.value;
				return Result.match(result, {
					onSuccess: (page) => {
						const match = selectByName(capture, page.data);
						return match === undefined
							? { model: evo(model, { resolving: () => false }) }
							: select(model, { id: match.id, by: "name" }, shared);
					},
					onFailure: (error) => ({
						model: evo(model, {
							resolving: () => false,
							error: () => Option.some(toMessage(error)),
						}),
					}),
				});
			},
			CompletedFetchRecord: ({ nodeId, generation, result }) =>
				isStale(model, nodeId, generation, shared)
					? { model }
					: {
							model: evo(model, { record: (record) => settle(record, result) }),
						},
			CompletedFetchEntries: ({ nodeId, generation, result }) =>
				isStale(model, nodeId, generation, shared)
					? { model }
					: {
							model: evo(model, {
								entries: (entries) =>
									settle(
										entries,
										Result.map(result, (page) => page.data),
									),
							}),
						},
			CompletedCheckImage: ({ nodeId, generation, result }) =>
				isStale(model, nodeId, generation, shared)
					? { model }
					: { model: evo(model, { image: (image) => settle(image, result) }) },
			GotPickerMessage: ({ message }) => {
				const folded = foldPicker(shared)(model, message);
				const chosen = folded.model.picker.chosen;
				if (
					Option.isNone(chosen) ||
					Option.exists(model.picker.chosen, (c) => c.id === chosen.value.id)
				)
					return folded;
				const selected = select(
					folded.model,
					{ id: chosen.value.id, by: "search" },
					shared,
				);
				return {
					model: selected.model,
					commands: [...(folded.commands ?? []), ...(selected.commands ?? [])],
				};
			},
			ClickedNotThisPerson: () => ({
				model: evo(model, {
					selected: () => Option.none(),
					record: () => AsyncData.Idle(),
					entries: () => AsyncData.Idle(),
					image: () => AsyncData.Idle(),
					synced: () => Option.none(),
					photoFailed: () => false,
					conflict: () => false,
					stale: () => false,
				}),
			}),
			ClickedCreate: () =>
				Option.isSome(model.inFlight) || Option.isNone(model.payload)
					? { model }
					: {
							model: evo(model, {
								inFlight: () => Option.some("create" as const),
								error: () => Option.none(),
							}),
							commands: [
								Create({
									capture: model.payload.value,
									generation: shared.generation,
								}),
							],
						},
			CompletedCreate: ({ generation, result }) => {
				if (generation !== shared.generation) return { model };
				return Result.match(result, {
					onSuccess: (id) =>
						Update.combine(model, [
							(m) =>
								select(
									evo(m, { synced: () => Option.map(m.payload, syncedFields) }),
									{ id, by: "created" },
									shared,
								),
							// `select` already fetches the new record; only the photo step remains.
							maybeImportPhoto(shared),
						]),
					onFailure: (error) => failedWrite(model, error, shared),
				});
			},
			ClickedLink: () =>
				withBase(model, (id, record, capture) => ({
					model: evo(model, {
						inFlight: () => Option.some("link" as const),
						error: () => Option.none(),
						stale: () => false,
					}),
					commands: [
						Link({
							nodeId: id,
							record: linkOverlay(record, capture),
							ifLastUpdated: record.timestamp,
							generation: shared.generation,
						}),
					],
				})),
			CompletedLink: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: () =>
								refetchRecord(shared)(
									evo(model, { inFlight: () => Option.none() }),
								),
							onFailure: (error) => failedWrite(model, error, shared),
						}),
			ClickedSync: () =>
				withBase(model, (id, record, capture) => ({
					model: evo(model, {
						inFlight: () => Option.some("sync" as const),
						error: () => Option.none(),
						photoFailed: () => false,
						stale: () => false,
					}),
					commands: [
						SyncPatch({
							nodeId: id,
							record: syncOverlay(record, capture),
							ifLastUpdated: record.timestamp,
							generation: shared.generation,
						}),
					],
				})),
			CompletedSyncPatch: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: () =>
								afterFieldsSaved(shared)(
									evo(model, {
										synced: () => Option.map(model.payload, syncedFields),
									}),
								),
							onFailure: (error) => failedWrite(model, error, shared),
						}),
			CompletedPhoto: ({ nodeId, generation, result }) =>
				isStale(model, nodeId, generation, shared)
					? { model }
					: refetchRecord(shared)(
							evo(model, {
								inFlight: () => Option.none(),
								photoFailed: () => Result.isFailure(result),
							}),
						),
			ClickedDismissError: () => ({
				model: evo(model, { error: () => Option.none() }),
			}),
		}),
};
